import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiSearch, FiEdit2, FiTrash2, FiStar, FiX } from 'react-icons/fi';
import { FaRegCalendarAlt, FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import { Modal, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Rating } from '@mui/material';
import BackgroundColor from './BackgroundColor';
import { useDispatch, useSelector } from 'react-redux';
import { getAllBlogs } from '../Redux/Slice/blog.slice';
import { getBlogReviews, createBlogReview, updateBlogReview, deleteBlogReview, clearReviews } from '../Redux/Slice/blogReview.slice';
import BlogSkeleton from './skeleton-loaders/BlogSkeleton';

export default function Blog() {

    const dispatch = useDispatch();
    const allBlogs = useSelector((state) => state.blog.blogs);
    const loading = useSelector((state) => state.blog.loading);
    const currentUser = useSelector((state) => state.auth.user);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    // Review state
    const reviews = useSelector((state) => state.blogReview.reviews);
    // const userReview = useSelector((state) => state.blogReview.userReview); // Derived locally now
    const averageRating = useSelector((state) => state.blogReview.averageRating);
    const totalReviews = useSelector((state) => state.blogReview.totalReviews);
    const reviewLoading = useSelector((state) => state.blogReview.loading);

    // Derive user review from reviews list
    const userReview = React.useMemo(() => {
        if (!currentUser || !reviews.length) return null;
        return reviews.find(review =>
            (review.user?._id === currentUser.id || review.user?._id === currentUser._id) ||
            (review.user === currentUser.id || review.user === currentUser._id)
        );
    }, [reviews, currentUser]);

    const [singleBlog, setSingleBlog] = useState(null);
    const [index, setIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState('');

    // Review form state
    const [reviewForm, setReviewForm] = useState({
        rating: 5,
        comment: ''
    });
    const [isEditMode, setIsEditMode] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Modal states
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // Store review data for each blog (for sidebar)
    const [blogReviewsData, setBlogReviewsData] = useState({});

    // Pagination state
    const [page, setPage] = useState(1);

    useEffect(() => {
        document.title = 'Blog • YOYO';
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (allBlogs.length > 0) {
            setSingleBlog(allBlogs[0]);
        }
    }, [allBlogs]);

    useEffect(() => {
        dispatch(getAllBlogs())
    }, [dispatch])


    // Extract review data from blogs (now included in getAllBlogs response)
    useEffect(() => {
        if (allBlogs.length > 0) {
            const reviewsMap = {};
            allBlogs.forEach(blog => {
                if (blog.totalReviews !== undefined && blog.averageRating !== undefined) {
                    reviewsMap[blog._id] = {
                        averageRating: blog.averageRating,
                        totalReviews: blog.totalReviews
                    };
                }
            });
            setBlogReviewsData(reviewsMap);
        }
    }, [allBlogs]);

    useEffect(() => {
        setIndex(0);
        setExpanded(false);
        setPage(1);
    }, [singleBlog]);

    const title = singleBlog?.title;
    const longText = singleBlog?.content;
    const isLongText = longText && longText.length > 300;
    const images = singleBlog?.blog_images?.map((image) => image?.url);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with zero if needed
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()]; // Get month name
        const year = date.getFullYear(); // Get full year
        return `${day}, ${month}, ${year}`; // Return formatted date
    };

    const author = { name: singleBlog?.author, date: formatDate(singleBlog?.createdAt) };

    const prev = () => setIndex((p) => (p - 1 + images.length) % images.length);
    const next = () => setIndex((p) => (p + 1) % images.length);

    // Function to handle blog selection
    const handleBlogSelect = (blog) => {
        setSingleBlog(blog);
    };

    // Fetch reviews when blog changes
    useEffect(() => {
        if (singleBlog?._id) {
            dispatch(getBlogReviews(singleBlog._id));
        }
        return () => {
            dispatch(clearReviews());
        };
    }, [singleBlog, dispatch]);

    // Update form when user review changes
    useEffect(() => {
        if (userReview && isEditMode) {
            setReviewForm({
                rating: userReview.rating,
                comment: userReview.comment
            });
        }
    }, [userReview, isEditMode]);

    // Validate review form
    const validateReviewForm = () => {
        const errors = {};

        if (!reviewForm.comment.trim()) {
            errors.comment = 'Review comment is required';
        } else if (reviewForm.comment.trim().length < 3) {
            errors.comment = 'Review must be at least 3 characters long';
        } else if (reviewForm.comment.trim().length > 1000) {
            errors.comment = 'Review must not exceed 1000 characters';
        }

        if (!reviewForm.rating || reviewForm.rating < 1 || reviewForm.rating > 5) {
            errors.rating = 'Please select a rating between 1 and 5';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle review form submission
    const handleReviewSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated) {
            alert('Please login to submit a review');
            setReviewModalOpen(false);
            return;
        }

        if (!validateReviewForm()) {
            return;
        }

        const reviewData = {
            blogId: singleBlog._id,
            rating: Number(reviewForm.rating),
            comment: reviewForm.comment.trim()
        };

        if (isEditMode && userReview) {
            await dispatch(updateBlogReview({ id: userReview._id, reviewData }));
            setIsEditMode(false);
        } else {
            await dispatch(createBlogReview(reviewData));
        }

        setReviewForm({ rating: 5, comment: '' });
        setFormErrors({});
        setReviewModalOpen(false);
    };

    // Handle edit review
    const handleEditReview = () => {
        setIsEditMode(true);
        setReviewForm({
            rating: userReview.rating,
            comment: userReview.comment
        });
        setReviewModalOpen(true);
    };

    // Handle delete review - open confirmation dialog
    const handleDeleteReview = () => {
        setDeleteConfirmOpen(true);
    };

    // Confirm delete review
    const confirmDeleteReview = async () => {
        await dispatch(deleteBlogReview(userReview._id));
        setReviewForm({ rating: 5, comment: '' });
        setIsEditMode(false);
        setDeleteConfirmOpen(false);
    };

    // Cancel edit mode
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setReviewForm({ rating: 5, comment: '' });
        setFormErrors({});
        setReviewModalOpen(false);
    };

    // Open review modal for new review
    const handleOpenReviewModal = () => {
        if (!isAuthenticated) {
            alert('Please login to write a review');
            return;
        }
        setIsEditMode(false);
        setReviewForm({ rating: 5, comment: '' });
        setFormErrors({});
        setReviewModalOpen(true);
    };


    const topArticles = allBlogs?.filter((blog) => blog._id !== singleBlog?._id) || [];

    const filteredTop = topArticles?.filter((a) => {
        const titleMatch = a.title.toLowerCase().includes(query.toLowerCase());
        const dateMatch = formatDate(a.createdAt).toLowerCase().includes(query.toLowerCase());
        return titleMatch || dateMatch;
    });

    if (loading || !allBlogs || allBlogs.length === 0) {
        return (
            <BackgroundColor>
                <BlogSkeleton />
            </BackgroundColor>
        );
    }

    return (
        <BackgroundColor>
            <section className="pt-24 pb-16">
                <div className="mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%]">
                    <motion.section
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-3xl md:text-5xl font-extrabold text-white">Latest Fresh From the <span className='gradient-text'>Gameverse</span> </h1>

                        <p className="text-gray-400 my-6">
                            Stay updated with the latest gaming news, trends, and guides from Yoyo Game Store.
                        </p>
                    </motion.section>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <motion.div
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="lg:col-span-2"
                        >
                            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#121212] shadow-2xl">
                                {/* Hero slider */}
                                <div className="relative">
                                    <div className="aspect-[21/9] w-full overflow-hidden">
                                        <AnimatePresence mode="wait" initial={false}>
                                            <motion.img
                                                key={`${singleBlog?.id || 'default'}-${index}`}
                                                src={images?.[index]}
                                                alt={title}
                                                initial={{ opacity: 0.4, scale: 1.02 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0.2, scale: 1.02 }}
                                                transition={{ duration: 0.5 }}
                                                className="w-full h-full object-cover"
                                            />
                                        </AnimatePresence>
                                    </div>

                                    {/* Arrows */}
                                    {images?.length > 1 && (
                                        <>
                                            <button
                                                aria-label="Previous image"
                                                onClick={prev}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition"
                                            >
                                                <FiChevronLeft size={22} />
                                            </button>
                                            <button
                                                aria-label="Next image"
                                                onClick={next}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition"
                                            >
                                                <FiChevronRight size={22} />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="px-6 md:px-10 pb-8 pt-6">
                                    <h1 className="font-serif text-lg md:text-3xl text-white tracking-wide">
                                        {title}
                                    </h1>

                                    <div className={`mt-4 text-sm md:text-[15px] leading-7 text-white/85 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}>
                                        {longText}
                                    </div>
                                    {isLongText && (
                                        <button
                                            onClick={() => setExpanded((v) => !v)}
                                            className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
                                        >
                                            {expanded ? 'Show Less' : 'Show More'}
                                        </button>
                                    )}

                                    {/* Meta */}
                                    <div className="mt-6 flex items-center gap-3 text-white/80">
                                        <div className="min-w-8 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-base">
                                            {author.name?.[0].toUpperCase()}
                                        </div>
                                        <div className="text-xs md:text-sm">
                                            <div>by <span className='capitalize'>{author.name}</span></div>
                                        </div>
                                        <div className="mx-2 h-4 w-px bg-white/20" />
                                        <div className="flex items-center gap-1 whitespace-nowrap text-xs md:text-sm">
                                            <FaRegCalendarAlt className="text-white/70" />
                                            <span>{author.date}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            {/* Reviews Section */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                className="mt-8 rounded-xl border border-white/10 bg-[#121212] shadow-2xl overflow-hidden"
                            >
                                {/* Reviews Header */}
                                <div className="px-6 md:px-10 py-4 border-b border-white/10">
                                    <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div>
                                            <h2 className="text-lg font-bold text-white mb-1">Reviews</h2>
                                            <div className="flex items-center gap-4">
                                                {totalReviews > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex items-center gap-0.5">
                                                            <Rating
                                                                name="half-rating-read"
                                                                value={averageRating}
                                                                precision={0.01}
                                                                readOnly
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiRating-iconFilled': {
                                                                        color: '#facc15', // yellow-400
                                                                    },
                                                                    '& .MuiRating-iconEmpty': {
                                                                        color: '#6b7280', // gray-500
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <span className="text-sm font-semibold text-yellow-400">
                                                            {averageRating}
                                                        </span>
                                                        <span className="text-xs text-white/50 ml-1">
                                                            ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-white/60">No reviews yet</span>
                                                )}
                                            </div>
                                        </div>
                                        {/* Write Review Button */}
                                        {isAuthenticated && !userReview && (
                                            <button
                                                onClick={handleOpenReviewModal}
                                                className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium hover:from-purple-700 hover:to-indigo-700 transition-all"
                                            >
                                                Write Review
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Reviews List */}
                                <div className="px-6 md:px-10 py-6">
                                    {reviewLoading && reviews.length === 0 ? (
                                        <div className="text-center py-8">
                                            <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                            <p className="text-white/60 mt-4">Loading reviews...</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="space-y-6">
                                                {(() => {
                                                    // Filter out user review from general reviews to avoid duplication if present
                                                    const otherReviews = reviews.filter(r => !userReview || r._id !== userReview._id);

                                                    // Combine user review (if exists) at the top
                                                    const allReviews = userReview
                                                        ? [userReview, ...otherReviews]
                                                        : otherReviews;

                                                    // Pagination
                                                    const reviewsPerPage = 10;
                                                    const totalPages = Math.ceil(allReviews.length / reviewsPerPage);
                                                    const displayedReviews = allReviews.slice((page - 1) * reviewsPerPage, page * reviewsPerPage);

                                                    if (allReviews.length === 0) {
                                                        return (
                                                            <div className="text-center py-8">
                                                                <p className="text-white/60">No reviews yet. Be the first to review!</p>
                                                            </div>
                                                        )
                                                    }

                                                    return (
                                                        <>
                                                            {displayedReviews.map((review, idx) => {
                                                                const isUserReview = userReview && review._id === userReview._id;

                                                                return (
                                                                    <motion.div
                                                                        key={review._id}
                                                                        initial={{ opacity: 0, y: 10 }}
                                                                        animate={{ opacity: 1, y: 0 }}
                                                                        transition={{ duration: 0.3, delay: idx * 0.05 }}
                                                                        className="pb-6 border-b border-white/10 last:border-0"
                                                                    >
                                                                        <div className="flex items-start justify-between gap-4">
                                                                            <div className="flex items-start gap-4 flex-1">
                                                                                <div className="min-w-10 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                                                                    {review.userName?.[0]?.toUpperCase() || review.user?.userName?.[0]?.toUpperCase() || 'U'}
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <div className="flex items-center justify-between gap-2 mb-2">
                                                                                        <div>
                                                                                            <h4 className="text-white font-medium capitalize flex items-center gap-2">
                                                                                                {review.userName || review.user?.userName || 'Anonymous'}
                                                                                                {isUserReview && (
                                                                                                    <span className="text-[10px] bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/30">
                                                                                                        You
                                                                                                    </span>
                                                                                                )}
                                                                                            </h4>
                                                                                            <div className="flex items-center gap-2 mt-1">
                                                                                                <Rating
                                                                                                    name="read-only"
                                                                                                    value={review.rating}
                                                                                                    precision={0.5}
                                                                                                    readOnly
                                                                                                    size="small"
                                                                                                    sx={{
                                                                                                        fontSize: '1rem',
                                                                                                        '& .MuiRating-iconFilled': {
                                                                                                            color: '#facc15', // yellow-400
                                                                                                        },
                                                                                                        '& .MuiRating-iconEmpty': {
                                                                                                            color: '#6b7280', // gray-500
                                                                                                        }
                                                                                                    }}
                                                                                                />
                                                                                                <span className="text-xs text-white/50">
                                                                                                    {formatDate(review.createdAt)}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">
                                                                                        {review.comment}
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            {/* Action Buttons for User Review */}
                                                                            {
                                                                                isUserReview && (
                                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                                        <button
                                                                                            onClick={handleEditReview}
                                                                                            className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-all"
                                                                                            title="Edit review"
                                                                                        >
                                                                                            <FiEdit2 size={16} />
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={handleDeleteReview}
                                                                                            className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                                                                            title="Delete review"
                                                                                        >
                                                                                            <FiTrash2 size={16} />
                                                                                        </button>
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        </div >
                                                                    </motion.div>
                                                                );
                                                            })}

                                                            {/* Pagination Controls */}
                                                            {totalPages > 1 && (
                                                                <div className="flex items-center justify-center gap-4 mt-8 pt-4 border-t border-white/5">
                                                                    <button
                                                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                                                        disabled={page === 1}
                                                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${page === 1
                                                                            ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                                                            : 'bg-white/10 text-white hover:bg-white/20'
                                                                            }`}
                                                                    >
                                                                        Previous
                                                                    </button>
                                                                    <span className="text-white/60 text-sm">
                                                                        Page {page} of {totalPages}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                                        disabled={page === totalPages}
                                                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${page === totalPages
                                                                            ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                                                            : 'bg-white/10 text-white hover:bg-white/20'
                                                                            }`}
                                                                    >
                                                                        Next
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>

                        {/* Sidebar */}
                        <motion.div
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                        >
                            <aside className="lg:sticky lg:top-28 h-fit space-y-6">
                                <div className="rounded-xl border border-white/10 bg-[#1111115d] p-4">
                                    <h3 className="text-lg font-semibold mb-3">Blog Search</h3>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search"
                                            className="w-full rounded-md bg-black/50 border border-white/10 pl-3 pr-10 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60" />
                                    </div>
                                </div>

                                <div className="rounded-xl border border-white/10 bg-[#1111115d] p-4">
                                    <h3 className="text-lg font-semibold mb-3">Other Blog</h3>
                                    <div className="divide-y divide-white/10 max-h-[48.5vh] overflow-x-hidden overflow-y-auto custom-scrollbar">
                                        {filteredTop.length === 0 && (
                                            <div className="py-3 flex items-center gap-3">
                                                <p className="text-white/60 text-sm">No blogs found</p>
                                            </div>
                                        )}
                                        {filteredTop.map((blog, blogIndex) => {
                                            const blogReview = blogReviewsData[blog._id];
                                            const hasReviews = blogReview && blogReview.totalReviews > 0;

                                            return (
                                                <div
                                                    key={blogIndex}
                                                    className="py-3 flex items-start gap-3 hover:bg-white/5 rounded-lg px-2 -mx-2 transition-colors cursor-pointer"
                                                    onClick={() => handleBlogSelect(blog)}
                                                >
                                                    <img
                                                        src={blog.blog_images?.[0]?.url}
                                                        alt={blog.title}
                                                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                                                    />
                                                    <div className="min-w-0 flex-1 flex items-start justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-sm font-medium leading-snug text-white hover:text-purple-300 transition-colors truncate">
                                                                {blog.title}
                                                            </div>
                                                            <div className="text-xs text-white/60 mt-1">
                                                                {formatDate(blog.createdAt)}
                                                            </div>
                                                        </div>
                                                        {/* {hasReviews && (
                                                            <div className="flex items-center gap-1 flex-shrink-0 pr-2">
                                                                <span className="text-sm text-yellow-400 font-medium mr-1">
                                                                    {blogReview.averageRating}
                                                                </span>
                                                                <Rating
                                                                    name="sidebar-rating"
                                                                    value={blogReview.averageRating}
                                                                    precision={0.01}
                                                                    readOnly
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: '0.8rem',
                                                                        '& .MuiRating-iconFilled': { color: '#facc15' },
                                                                        '& .MuiRating-iconEmpty': { color: '#6b7280' }
                                                                    }}
                                                                />
                                                                <span className="text-xs text-white/50">
                                                                    ( {blogReview.totalReviews} Reviews )
                                                                </span>
                                                            </div>
                                                        )} */}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </aside>
                        </motion.div>
                    </div>
                </div >
            </section >

            {/* Review Modal */}
            <Modal
                open={reviewModalOpen}
                onClose={handleCancelEdit}
                aria-labelledby="review-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '500px', md: '600px' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: '#1a1a1a',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    p: 0,
                    '& *': { cursor: 'none !important' },
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-track': { background: '#121212' },
                    '&::-webkit-scrollbar-thumb': { background: '#6b21a8', borderRadius: '4px' },
                }}>
                    {/* Modal Header */}
                    <div style={{
                        padding: '24px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <h2 id="review-modal-title" style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: 'white',
                            margin: 0
                        }}>
                            {isEditMode ? 'Edit Your Review' : 'Write a Review'}
                        </h2>
                        <button
                            onClick={handleCancelEdit}
                            style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'none'
                            }}
                        >
                            <FiX size={20} />
                        </button>
                    </div>

                    {/* Modal Body */}
                    <form onSubmit={handleReviewSubmit} style={{ padding: '24px' }}>
                        {/* Rating */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'rgba(255, 255, 255, 0.8)',
                                marginBottom: '8px'
                            }}>
                                Rating <span style={{ color: '#f87171' }}>*</span>
                            </label>
                            <Rating
                                name="half-rating"
                                value={Number(reviewForm.rating)}
                                precision={0.5}
                                size="large"
                                onChange={(event, newValue) => {
                                    setReviewForm({ ...reviewForm, rating: newValue });
                                    setFormErrors({ ...formErrors, rating: '' });
                                }}
                                sx={{
                                    '& .MuiRating-iconFilled': {
                                        color: '#facc15', // yellow-400
                                    },
                                    '& .MuiRating-iconHover': {
                                        color: '#facc15', // yellow-400
                                    },
                                    '& .MuiRating-iconEmpty': {
                                        color: '#6b7280', // gray-500
                                    }
                                }}
                            />
                            {formErrors.rating && (
                                <p style={{ marginTop: '4px', fontSize: '0.875rem', color: '#f87171' }}>
                                    {formErrors.rating}
                                </p>
                            )}
                        </div>

                        {/* Comment */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'rgba(255, 255, 255, 0.8)',
                                marginBottom: '8px'
                            }}>
                                Your Review <span style={{ color: '#f87171' }}>*</span>
                            </label>
                            <textarea
                                value={reviewForm.comment}
                                onChange={(e) => {
                                    setReviewForm({ ...reviewForm, comment: e.target.value });
                                    setFormErrors({ ...formErrors, comment: '' });
                                }}
                                placeholder="Share your thoughts about this blog..."
                                rows={5}
                                style={{
                                    width: '100%',
                                    borderRadius: '8px',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    padding: '12px 16px',
                                    color: 'white',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    resize: 'none',
                                    cursor: 'none'
                                }}
                            />
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: '4px'
                            }}>
                                {formErrors.comment && (
                                    <p style={{ fontSize: '0.875rem', color: '#f87171' }}>
                                        {formErrors.comment}
                                    </p>
                                )}
                                <p style={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    marginLeft: 'auto'
                                }}>
                                    {reviewForm.comment.length}/1000
                                </p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                type="submit"
                                disabled={reviewLoading}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    background: 'linear-gradient(to right, #9333ea, #4f46e5)',
                                    color: 'white',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'none',
                                    opacity: reviewLoading ? 0.5 : 1
                                }}
                            >
                                {reviewLoading ? 'Submitting...' : isEditMode ? 'Update Review' : 'Submit Review'}
                            </button>
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    fontWeight: '500',
                                    border: 'none',
                                    cursor: 'none'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </Box>
            </Modal >

            {/* Delete Confirmation Dialog */}
            < Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)
                }
                PaperProps={{
                    sx: {
                        bgcolor: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        '& *': { cursor: 'none !important' }
                    }
                }}
            >
                <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
                    Delete Review
                </DialogTitle>
                <DialogContent>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
                        Are you sure you want to delete your review? This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                            cursor: 'none',
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteReview}
                        sx={{
                            color: 'white',
                            bgcolor: '#ef4444',
                            '&:hover': { bgcolor: '#dc2626' },
                            cursor: 'none',
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog >

            {/* Global CSS to hide cursor */}
            < style jsx global > {`
                * {
                    cursor: none !important;
                }
                input, textarea, button, a, select {
                    cursor: none !important;
                }
            `}</style >
        </BackgroundColor >
    );
}